import BuildIcon from '@mui/icons-material/Build';
import BusinessIcon from '@mui/icons-material/Business';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { useState, useEffect } from 'react';
import Header from '../../components/dashboard/Header';
import HostSideDrawer from '../../components/dashboard/HostSideDrawer';
import { useAuth } from '../../context/AuthContext';
import { botelService, Botel } from '../../services/api/botelService';
import api from '../../services/api/axiosConfig';
import useCurrentUser from '../../hooks/useCurrentUser';

interface Order {
  id: string;
  user_id: string;
  order_number: string;
  total_amount: number;
  payment_status: string;
  order_details: {
    partnerId: string;
    partnerName?: string;
    units: number;
    unitPrice: number;
    productId: string;
    version: string;
    email: string;
    phone: string;
  };
  shipping_address: any;
  billing_address: any;
  paid_at: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  created_at: string;
  updated_at: string;
  user: {
    id: string;
    email: string;
    name: string;
  };
}

interface OrdersResponse {
  success: boolean;
  totalItems: number;
  data: Order[];
  currentPage: number;
  totalPages: number;
  pageSize: number;
}

const BotelManagerPage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const toggleSidebar = () => setSidebarOpen((open) => !open);
  const { user } = useAuth();
  const { userId } = useCurrentUser();
  const [botels, setBotels] = useState<Botel[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);

  useEffect(() => {
    const fetchBotels = async () => {
      try {
        setLoading(true);
        const response = await api.get('/partners/my-botels');
        // Handle response - could be response.data.data or response.data depending on API structure
        const botelsData = response.data?.data || response.data || [];
        setBotels(Array.isArray(botelsData) ? botelsData : []);
      } catch (error) {
        console.error('Failed to fetch botels:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBotels();
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!userId) {
        setOrdersLoading(false);
        return;
      }

      try {
        setOrdersLoading(true);
        const response = await api.get<OrdersResponse>('/partners/orders');
        if (response.data.success) {
          // Filter orders by logged-in user's userId
          const userOrders = response.data.data.filter(order => order.user_id === userId);
          setOrders(userOrders);
        }
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchOrders();
  }, [userId]);

  // Calculate stats from orders
  const activeBookings = orders
    .filter(order => order.payment_status === 'paid' || order.payment_status === 'pending')
    .reduce((sum, order) => sum + (order.order_details?.units || 0), 0);

  const stats = {
    totalBotels: {
      count: orders.length,
      change: `${orders.length} Orders`,
      isPositive: true
    },
    activeBookings: {
      count: activeBookings,
      change: `${orders.filter(o => o.payment_status === 'paid' || o.payment_status === 'pending').length} Active Orders`
    },
    monthlyRevenue: {
      amount: '$1500.00',
      change: '+12.5% From Last Month'
    },
    securityStatus: {
      status: 'All Clear',
      systems: 'ADT Systems Active'
    }
  };


  const maintenanceRequests = [
    {
      id: 'BOT-001',
      issue: 'Lock Issue',
      reportedTime: 'Reported 2h ago',
      status: 'In Progress'
    },
    {
      id: 'BOT-002',
      issue: 'Maintenance Check',
      reportedTime: 'Reported 1d ago',
      status: 'Completed'
    }
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <Header onToggleSidebar={toggleSidebar} open={sidebarOpen} />
      <HostSideDrawer open={sidebarOpen} />
      <Box component="main" sx={{ flexGrow: 1 }}>
    <Box sx={{ 
      flexGrow: 1, 
      p: { xs: 2, md: 3 }, 
      background: '#151A23', 
      minHeight: '100vh', 
      color: '#fff',
      marginTop: 8,
      overflowX: 'hidden',
      width: '100%',
      maxWidth: '100%'
    }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <BusinessIcon sx={{ fontSize: 32, color: '#4A90E2' }} />
              <Box>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#fff' }}>
                  Botel Management
                </Typography>
                <Typography variant="body1" sx={{ color: '#b0b8c1' }}>
                  Monitor and manage your botel network
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ background: '#232B3E', borderRadius: 3, height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <BusinessIcon sx={{ color: '#4A90E2', mr: 1 }} />
                    <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                      Total Botels
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: '#fff', mb: 1 }}>
                    {stats.totalBotels.count}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon sx={{ color: '#4CAF50', fontSize: 16, mr: 0.5 }} />
                    <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                      {stats.totalBotels.change}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ background: '#232B3E', borderRadius: 3, height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      width: 24, 
                      height: 24, 
                      background: '#4CAF50', 
                      borderRadius: 1, 
                      mr: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography variant="caption" sx={{ color: '#fff', fontWeight: 'bold' }}>
                        A
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                      Active Bookings
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: '#fff', mb: 1 }}>
                    {stats.activeBookings.count}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon sx={{ color: '#4CAF50', fontSize: 16, mr: 0.5 }} />
                    <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                      {stats.activeBookings.change}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ background: '#232B3E', borderRadius: 3, height: '100%', position: 'relative' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: '#4CAF50', mr: 1 }}>
                      $
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                      Monthly Revenue
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: '#fff', mb: 1 }}>
                    {stats.monthlyRevenue.amount}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon sx={{ color: '#4CAF50', fontSize: 16, mr: 0.5 }} />
                    <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                      {stats.monthlyRevenue.change}
                    </Typography>
                  </Box>
                </CardContent>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    borderRadius: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    backdropFilter: 'blur(2px)',
                    pointerEvents: 'none',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#9ca3af',
                      fontSize: 24,
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    Coming Soon
                  </Typography>
                  <Typography
                    sx={{
                      color: '#6b7280',
                      fontSize: 16,
                      textAlign: 'center',
                      px: 2,
                    }}
                  >
                    This feature will be available in a future release
                  </Typography>
                </Box>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ background: '#232B3E', borderRadius: 3, height: '100%', position: 'relative' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SecurityIcon sx={{ color: '#4CAF50', mr: 1 }} />
                    <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                      Security Status
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: '#fff', mb: 1 }}>
                    {stats.securityStatus.status}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      background: '#4CAF50', 
                      borderRadius: '50%', 
                      mr: 1 
                    }} />
                    <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                      {stats.securityStatus.systems}
                    </Typography>
                  </Box>
                </CardContent>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    borderRadius: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    backdropFilter: 'blur(2px)',
                    pointerEvents: 'none',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#9ca3af',
                      fontSize: 24,
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    Coming Soon
                  </Typography>
                  <Typography
                    sx={{
                      color: '#6b7280',
                      fontSize: 16,
                      textAlign: 'center',
                      px: 2,
                    }}
                  >
                    This feature will be available in a future release
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Orders Section */}
          <Paper sx={{ p: 3, mb: 4, background: '#232B3E', borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff' }}>
                My Botels
              </Typography>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#b0b8c1', borderColor: '#4A5568' }}>
                      Botel Name
                    </TableCell>
                    <TableCell sx={{ color: '#b0b8c1', borderColor: '#4A5568' }}>
                      Location
                    </TableCell>
                    <TableCell sx={{ color: '#b0b8c1', borderColor: '#4A5568' }}>
                      Address
                    </TableCell>
                    <TableCell sx={{ color: '#b0b8c1', borderColor: '#4A5568' }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ color: '#b0b8c1', borderColor: '#4A5568' }}>
                      Mode
                    </TableCell>
                    <TableCell sx={{ color: '#b0b8c1', borderColor: '#4A5568' }}>
                      Created At
                    </TableCell>
                    <TableCell sx={{ color: '#b0b8c1', borderColor: '#4A5568' }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ color: '#fff', borderColor: '#4A5568', textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                          Loading botels...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : botels.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ color: '#fff', borderColor: '#4A5568', textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                          No botels found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    botels.map((botel) => (
                      <TableRow key={botel.id}>
                        <TableCell sx={{ color: '#fff', borderColor: '#4A5568' }}>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {botel.name}
                            </Typography>
                            {botel.description && (
                              <Typography variant="body2" sx={{ color: '#b0b8c1', fontSize: '0.75rem' }}>
                                {botel.description.substring(0, 50)}...
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: '#fff', borderColor: '#4A5568' }}>
                          {botel.location || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ color: '#fff', borderColor: '#4A5568' }}>
                          {botel.location_address || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ color: '#fff', borderColor: '#4A5568' }}>
                          <Box
                            sx={{
                              background: botel.device_status === 'online' ? '#4CAF50' : 
                                         botel.device_status === 'offline' ? '#757575' : 
                                         botel.device_status === 'maintenance' ? '#FF9800' : '#F44336',
                              color: '#fff',
                              px: 2,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: 12,
                              display: 'inline-block',
                              textTransform: 'capitalize'
                            }}
                          >
                            {botel.device_status || 'N/A'}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: '#fff', borderColor: '#4A5568' }}>
                          <Chip 
                            label={botel.mode || 'HYBRID'} 
                            size="small" 
                            color={botel.mode === 'STR' ? 'primary' : botel.mode === 'DELIVERY' ? 'secondary' : 'info'}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#fff', borderColor: '#4A5568' }}>
                          <Typography variant="body2">
                            {new Date(botel.created_at).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#b0b8c1', fontSize: '0.75rem' }}>
                            {new Date(botel.created_at).toLocaleTimeString()}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ color: '#fff', borderColor: '#4A5568' }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="This feature will be available in future release">
                              <span>
                                <IconButton 
                                  size="small" 
                                  disabled
                                  sx={{ color: '#b0b8c1' }}
                                >
                                  <SettingsIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="This feature will be available in future release">
                              <span>
                                <IconButton 
                                  size="small" 
                                  disabled
                                  sx={{ color: '#b0b8c1' }}
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="This feature will be available in future release">
                              <span>
                                <IconButton 
                                  size="small" 
                                  disabled
                                  sx={{ color: '#b0b8c1' }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Bottom Section */}
          <Grid container spacing={3}>
            {/* Live Security Feed */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, background: '#232B3E', borderRadius: 3, height: '100%', position: 'relative' }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, color: '#fff' }}>
                  Live Security Feed
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box 
                      sx={{ 
                        position: 'relative',
                        background: '#181F2A',
                        borderRadius: 2,
                        height: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundImage: 'linear-gradient(45deg, #333 25%, transparent 25%), linear-gradient(-45deg, #333 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #333 75%), linear-gradient(-45deg, transparent 75%, #333 75%)',
                        backgroundSize: '20px 20px',
                        backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
                      }}
                    >
                      <IconButton 
                        sx={{ 
                          background: 'rgba(0,0,0,0.6)', 
                          color: '#fff',
                          '&:hover': {
                            background: 'rgba(0,0,0,0.8)'
                          }
                        }}
                      >
                        <PlayArrowIcon sx={{ fontSize: 32 }} />
                      </IconButton>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          position: 'absolute',
                          bottom: 8,
                          left: 8,
                          color: '#fff',
                          background: 'rgba(0,0,0,0.6)',
                          px: 1,
                          borderRadius: 1
                        }}
                      >
                        BOT-001 Live
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box 
                      sx={{ 
                        position: 'relative',
                        background: '#181F2A',
                        borderRadius: 2,
                        height: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundImage: 'linear-gradient(45deg, #333 25%, transparent 25%), linear-gradient(-45deg, #333 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #333 75%), linear-gradient(-45deg, transparent 75%, #333 75%)',
                        backgroundSize: '20px 20px',
                        backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
                      }}
                    >
                      <IconButton 
                        sx={{ 
                          background: 'rgba(0,0,0,0.6)', 
                          color: '#fff',
                          '&:hover': {
                            background: 'rgba(0,0,0,0.8)'
                          }
                        }}
                      >
                        <PlayArrowIcon sx={{ fontSize: 32 }} />
                      </IconButton>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          position: 'absolute',
                          bottom: 8,
                          left: 8,
                          color: '#fff',
                          background: 'rgba(0,0,0,0.6)',
                          px: 1,
                          borderRadius: 1
                        }}
                      >
                        BOT-002 Live
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    borderRadius: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    backdropFilter: 'blur(2px)',
                    pointerEvents: 'none',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#9ca3af',
                      fontSize: 24,
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    Coming Soon
                  </Typography>
                  <Typography
                    sx={{
                      color: '#6b7280',
                      fontSize: 16,
                      textAlign: 'center',
                      px: 2,
                    }}
                  >
                    This feature will be available in a future release
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Maintenance Requests */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, background: '#232B3E', borderRadius: 3, height: '100%', position: 'relative' }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, color: '#fff' }}>
                  Maintenance Requests
                </Typography>
                
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<BuildIcon />}
                  disabled
                  sx={{
                    mb: 3,
                    py: 2,
                    background: '#F44336',
                    '&:hover': { background: '#D32F2F' },
                    borderRadius: 2,
                    fontSize: 16
                  }}
                >
                  Request Maintenance
                </Button>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {maintenanceRequests.map((request, index) => (
                    <Box 
                      key={request.id}
                      sx={{ 
                        p: 2, 
                        background: '#181F2A', 
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#fff' }}>
                          {request.issue}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#b0b8c1' }}>
                          {request.id} • {request.reportedTime}
                        </Typography>
                      </Box>
                      <Chip 
                        label={request.status}
                        sx={{ 
                          background: request.status === 'Completed' ? '#4CAF50' : '#FF9800',
                          color: '#fff',
                          fontSize: 12
                        }} 
                      />
                    </Box>
                  ))}
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    borderRadius: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    backdropFilter: 'blur(2px)',
                    pointerEvents: 'none',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#9ca3af',
                      fontSize: 24,
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    Coming Soon
                  </Typography>
                  <Typography
                    sx={{
                      color: '#6b7280',
                      fontSize: 16,
                      textAlign: 'center',
                      px: 2,
                    }}
                  >
                    This feature will be available in a future release
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Footer */}
          <Box sx={{ 
            mt: 4, 
            py: 2, 
            borderTop: '1px solid #232B3E', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            color: '#b0b8c1'
          }}>
            <Typography sx={{ fontSize: 14 }}>
              © 2025 BOTEL. All Rights Reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button sx={{ color: '#b0b8c1', textTransform: 'none' }}>
                Help
              </Button>
              <Button sx={{ color: '#b0b8c1', textTransform: 'none' }}>
                Contact
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BotelManagerPage;
